import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getToken } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Coins } from "lucide-react";
import { TokenLogo } from "@/components/common/TokenLogo";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });
const truncate = (s) => `${s.slice(0, 8)}...${s.slice(-6)}`;

export const HoldersTable = ({ holders, symbol, highlight }) => (
  <table data-testid="token-holders-table" className="w-full text-sm">
    <thead>
      <tr className="text-xs text-zinc-500 text-left">
        <th className="py-2 font-normal">Holder</th>
        <th className="py-2 font-normal text-right">Balance</th>
        <th className="py-2 font-normal text-right">Share</th>
      </tr>
    </thead>
    <tbody>
      {holders.map((h) => (
        <tr key={h.address} className="border-t border-[#2A2411]">
          <td className="py-2">
            <Link to={`/address/${h.address}`} className="table-link font-mono text-xs">{truncate(h.address)}</Link>
            {highlight && h.address === highlight.toLowerCase() && <span className="ml-2 text-[10px] text-[#D4AF37]">YOU</span>}
          </td>
          <td className="py-2 text-right font-mono text-xs text-zinc-300">{fmt(h.balance)} {symbol}</td>
          <td className="py-2 text-right font-mono text-xs text-zinc-500">{h.share.toFixed(2)}%</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const TokenOverview = ({ address }) => {
  const { data, isLoading } = useQuery({ queryKey: ["token", address], queryFn: () => getToken(address), refetchInterval: 10000 });
  if (isLoading || !data) return null;
  const stats = [
    ["Name", data.name, "token-name"],
    ["Symbol", data.symbol, "token-symbol"],
    ["Total Supply", `${fmt(data.totalSupply)} ${data.symbol}`, "token-total-supply"],
    ["Decimals", data.decimals, "token-decimals"],
    ["Holders", data.holderCount, "token-holder-count"],
    ["Transfers", data.transferCount, "token-transfer-count"],
  ];
  return (
    <Card data-testid="token-overview" className="border-[#2A2411] bg-[#0A0A0A] mt-6">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-display flex items-center gap-2">
          <TokenLogo address={data.address} symbol={data.symbol} size="md" testId="token-logo" /> <Coins size={16} className="text-[#D4AF37]" /> ERC-20 Token
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {stats.map(([label, value, id]) => (
            <div key={label}>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">{label}</p>
              <p data-testid={id} className="font-display font-bold text-white break-all">{value}</p>
            </div>
          ))}
        </div>
        {data.holders.length > 0 && <HoldersTable holders={data.holders} symbol={data.symbol} />}
      </CardContent>
    </Card>
  );
};
